import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LeapV2EvidenceService, StoredEvidenceFile } from '../../../tutor-report-flows/tutor-report-leap-v2/services/leap-v2-evidence.service';
import { BaseLearningSectionComponent } from '../../../../shared/learning-section/base-learning-section/base-learning-section.component';
import { LearningSectionFlowControlHelperService } from '../../../../_helpers/learning-section-flow-control-helper/learning-section-flow-control-helper.service';
import { LeapV2SessionTimelineSection } from '../../../../_models/timeline-models/section-models/leap-v2-session-section.model';
import { ATTAINMENT_LABELS } from '../../../tutor-report-flows/tutor-report-leap-v2/leap-v2-attainment-bar/leap-v2-attainment-bar.component';
import { ENGAGEMENT_LABELS } from '../../../tutor-report-flows/tutor-report-leap-v2/leap-v2-engagement-bar/leap-v2-engagement-bar.component';

/**
 * Timeline card for a LEAP v2 (LearnerJourney) session (sectionType 'session'). Read-only, shaped on the
 * add-session-v2 model and rendered with the same scale sliders as the ILP timeline section, with a status
 * pill and a click-through to the session report screen. Replaces the old collapsed SessionSectionComponent.
 */
@Component({
  selector: 'leap-v2-session-section',
  templateUrl: './leap-v2-session-section.component.html',
  styleUrls: ['./leap-v2-session-section.component.css'],
  standalone: false,
})
export class LeapV2SessionSectionComponent extends BaseLearningSectionComponent {
  @Input() timelineSection: LeapV2SessionTimelineSection;

  /** Attainment slider ticks (0–4), shared with the add-session-v2 attainment bar. */
  readonly attainmentLabels = ATTAINMENT_LABELS;
  /** Engagement slider ticks (1–5), shared with the add-session-v2 engagement bar. */
  readonly engagementLabels = ENGAGEMENT_LABELS;

  constructor(
    protected readonly flowHelperService: LearningSectionFlowControlHelperService,
    public dialog: MatDialog,
    private router: Router,
    private evidenceService: LeapV2EvidenceService
  ) {
    super(flowHelperService, dialog);
    this.sectionTitle = 'Session';
  }

  /** Opens an evidence file through the API (its link is api/Evidence/{id}, not a blob URL). */
  openEvidence(event: Event, file: StoredEvidenceFile): void {
    event.preventDefault();
    this.evidenceService.open(file);
  }

  /** Effective status id (1 Saved, 2 Submitted, 3 Approved, 4 Rejected); falls back to approved/draft. */
  private effectiveStatusId(): number {
    return this.timelineSection?.statusId ?? (this.timelineSection?.approved ? 3 : 1);
  }

  statusLabel(): string {
    switch (this.effectiveStatusId()) {
      case 1: return 'Saved';
      case 2: return 'Submitted';
      case 3: return 'Approved';
      case 4: return 'Rejected';
      default: return '';
    }
  }

  statusClass(): string {
    switch (this.effectiveStatusId()) {
      case 1: return 'pr-status--saved';
      case 2: return 'pr-status--submitted';
      case 3: return 'pr-status--approved';
      case 4: return 'pr-status--rejected';
      default: return '';
    }
  }

  /** The report screen is keyed on the Orbit session; a session with no Orbit id has no report to open. */
  hasReport(): boolean {
    return (this.timelineSection?.orbitSessionId ?? 0) > 0;
  }

  openReport(): void {
    if (!this.hasReport()) {
      return;
    }
    this.router.navigate(['/add-session-v2', this.timelineSection.placementId, this.timelineSection.orbitSessionId], {
      state: {
        selectedSessionDate: { startDate: this.timelineSection.sectionDate },
        componentViewMode: 'View'
      }
    });
  }
}
